import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { IPaymentOption } from '../../../../types/@types/generated/contentful';
import { ImpacterResponse } from '../../../../types/ImpacterTypes';
import { DonationPlan } from '../../../../types/PaymentTypes';
import { GetCategoryResponse } from '../../../../utils/api';
import { Video } from '../../../../utils/api/videoApi';
import CheckoutContext from '../../../../utils/checkoutContext';
import { getStreamingSrc } from '../../../../utils/helpers/video';
import { breakpoints, colors, NAVBAR_HEIGHT_L, spacing } from '../../../../utils/styleguide';
import { ImageStyleProps } from '../../../contentful-elements/Image';
import KlarnaAttentionBanner from '../../banners/KlarnaAttentionBanner';
import HeadlinerWithFold from '../../HeadlinerWithFold';
import BackgroundVideoSection from '../../page-sections/BackgroundVideoSection';
import PropsComponent from '../../types/PropsComponent';
import { Col, Row } from '../12ColGridLayout';
import { Logo, Logos } from './Logos';
import KlarnaInlineWidget from './widgets/KlarnaInlineWidget';
import StripeInlineWidget from './widgets/StripeInlineWidget';

const Content = styled.section`
  min-height: 500px;
  max-height: 660px;
  height: 85vh;
`;

const ContentWrapper = styled.div<{
  floatingHeader?: boolean;
  showKlarnaAttentionBanner?: boolean;
}>`
  padding-top: ${({ floatingHeader }) => (floatingHeader ? NAVBAR_HEIGHT_L : 0)}px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const HeroContent = styled.div<{ showKlarnaAttentionBanner?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  flex-grow: 1;
`;

const DescriptionBlock = styled.div`
  margin-top: ${spacing[4]}px;
  ${breakpoints.tablet} {
    margin-top: ${spacing[6]}px;
  }
`;

const NameBlock = styled.div`
  margin-top: ${spacing[4]}px;
  ${breakpoints.tablet} {
    margin-top: ${spacing[6]}px;
  }
`;

const DonationBlock = styled.div`
  position: relative;
`;

const Displacement = styled.section`
  position: relative;
  z-index: 0;
  width: 100%;
`;

const BackgroundColor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.brightGrey};
  z-index: -2;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type HeroProps = {
  id?: string;
  title: ReactNode;
  description: ReactNode;
  name: ReactNode;
  backgroundColor?: string;
  backgroundLinear?: string;
  portraitBackgroundVideo?: Video;
  landscapeBackgroundVideo?: Video;
  impacters?: Pick<ImpacterResponse, 'id' | 'externalId' | 'nickname'>[];
  category?: GetCategoryResponse;
  initiator: string;
  affiliateName?: string;
  image?: PropsComponent<ImageStyleProps>;
  logosList?: Logo[];
  plan?: DonationPlan;
  paymentOption?: IPaymentOption;
  campaign?: string;
  thankYouPageSlugOverride?: string;
  overlay?: number;
  showKlarnaAttentionBanner?: boolean;
  floatingHeader?: boolean;
  donationProgramId?: string;
  partnerId?: string;
};

const HeroDonationLayout = ({
  id,
  title,
  description,
  name,
  backgroundColor,
  backgroundLinear,
  portraitBackgroundVideo,
  landscapeBackgroundVideo,
  impacters,
  category,
  affiliateName,
  initiator,
  image,
  logosList,
  paymentOption,
  campaign,
  thankYouPageSlugOverride,
  overlay = 30,
  showKlarnaAttentionBanner,
  floatingHeader,
  donationProgramId,
  partnerId,
}: HeroProps) => {
  const content = (
    <Content>
      <ContentWrapper floatingHeader={floatingHeader}>
        {showKlarnaAttentionBanner ? <KlarnaAttentionBanner /> : null}
        <HeroContent showKlarnaAttentionBanner={showKlarnaAttentionBanner}>
          <Row>
            <Col tablet={'2 / span 10'}>{title}</Col>
            <Col desktop={'4 / span 6'}>
              <DescriptionBlock>{description}</DescriptionBlock>
              {logosList && (
                <LogoWrapper>
                  <Logos logos={logosList} />
                </LogoWrapper>
              )}
              {name && <NameBlock>{name}</NameBlock>}
            </Col>
          </Row>
        </HeroContent>
      </ContentWrapper>
    </Content>
  );

  return (
    <CheckoutContext.Provider
      value={{
        grantmakerName: paymentOption?.fields.grantmakerName,
        affiliateName,
        campaign,
        initiator,
        donationProgramId,
        partnerId,
      }}
    >
      {landscapeBackgroundVideo && portraitBackgroundVideo ? (
        <BackgroundVideoSection
          videoLargeScreenUrl={getStreamingSrc({ video: landscapeBackgroundVideo })}
          videoSmallScreenUrl={getStreamingSrc({ video: portraitBackgroundVideo })}
          posterLargeScreenUrl={landscapeBackgroundVideo.metaData.posterSrc}
          posterSmallScreenUrl={portraitBackgroundVideo.metaData.posterSrc}
          aspectRatios={{ desktop: 0, tablet: 0, mobile: 0 }}
          showControls={false}
          hasOverlay
        >
          {content}
        </BackgroundVideoSection>
      ) : (
        <HeadlinerWithFold
          id={id}
          backgroundColor={image ? undefined : backgroundColor}
          backgroundLinear={backgroundLinear}
          Image={image}
          overlay={overlay}
        >
          <>{content}</>
        </HeadlinerWithFold>
      )}
      <DonationBlock>
        <Displacement id={id?.concat('donate')}>
          <Row>
            <Col tablet="4 / span 6">
              {paymentOption && paymentOption.fields.provider === 'klarna' ? (
                <KlarnaInlineWidget
                  impacters={impacters}
                  category={category}
                  initiator={initiator}
                  paymentOption={paymentOption}
                  campaign={campaign}
                  thankYouPageSlugOverride={thankYouPageSlugOverride}
                />
              ) : (
                <StripeInlineWidget
                  impacters={impacters || []}
                  category={category}
                  paymentOption={paymentOption}
                  campaign={campaign}
                  thankYouPageSlugOverride={thankYouPageSlugOverride}
                />
              )}
            </Col>
          </Row>
        </Displacement>
        <BackgroundColor />
      </DonationBlock>
    </CheckoutContext.Provider>
  );
};

export default HeroDonationLayout;
